import { storageService } from '@services/storage';
import { supabase } from '@services/supabase';

const BASE_KEY = '@recently_viewed_stocks';
const MAX_RECENTLY_VIEWED = 10;

export interface RecentlyViewedStock {
  ticker: string;
  companyName: string;
  viewedAt: string;
}

/**
 * Recently Viewed Service
 * Tracks stocks user has viewed (local storage for fast access)
 */
class RecentlyViewedService {
  private async keyForUser(): Promise<string> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return `${BASE_KEY}:${user?.id || 'anon'}`;
    } catch {
      return `${BASE_KEY}:anon`;
    }
  }
  /**
   * Get recently viewed stocks
   */
  async getRecentlyViewed(): Promise<RecentlyViewedStock[]> {
    try {
      const key = await this.keyForUser();
      const data = await storageService.getItem(key);
      if (!data) return [];

      const parsed: RecentlyViewedStock[] = JSON.parse(data);

      // Sort by most recent first
      return parsed.sort((a, b) => new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime());
    } catch (err) {
      console.error('Error fetching recently viewed:', err);
      return [];
    }
  }

  /**
   * Add stock to recently viewed
   * Automatically deduplicates and maintains max limit
   */
  async addRecentlyViewed(ticker: string, companyName: string): Promise<void> {
    try {
      const key = await this.keyForUser();
      const existing = await this.getRecentlyViewed();

      // Remove if already exists (we'll re-add with new timestamp)
      const filtered = existing.filter(item => item.ticker !== ticker);

      // Add to front
      const updated: RecentlyViewedStock[] = [
        {
          ticker,
          companyName,
          viewedAt: new Date().toISOString(),
        },
        ...filtered,
      ];

      // Keep only the most recent MAX_RECENTLY_VIEWED items
      const trimmed = updated.slice(0, MAX_RECENTLY_VIEWED);

      await storageService.setItem(key, JSON.stringify(trimmed));
    } catch (err) {
      console.error('Error adding recently viewed:', err);
    }
  }

  /**
   * Clear all recently viewed
   */
  async clearRecentlyViewed(): Promise<void> {
    try {
      const key = await this.keyForUser();
      await storageService.removeItem(key);
    } catch (err) {
      console.error('Error clearing recently viewed:', err);
    }
  }

  /**
   * Remove specific stock from recently viewed
   */
  async removeRecentlyViewed(ticker: string): Promise<void> {
    try {
      const key = await this.keyForUser();
      const existing = await this.getRecentlyViewed();
      const filtered = existing.filter(item => item.ticker !== ticker);
      await storageService.setItem(key, JSON.stringify(filtered));
    } catch (err) {
      console.error('Error removing recently viewed:', err);
    }
  }
}

export const recentlyViewedService = new RecentlyViewedService();
